import {
  Flex,
  IconButton,
  NumberInput,
  NumberInputField,
  Text,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";

export type PaginationButtonsProps = {
  totalItems: number;
  pageSize: number;
  currentPageIndex: number;
  setCurrentPageIndex: (page: number) => void;
  siblingPages?: number;
};

export function PaginationButtons({
  totalItems,
  pageSize,
  currentPageIndex,
  setCurrentPageIndex,
}: PaginationButtonsProps): React.ReactElement | null {
  const totalPages = pageSize > 0 ? Math.ceil(totalItems / pageSize) : 0;

  const currentPageNumber = currentPageIndex + 1;

  // De-couple currentPageIndex from the input field value, to allow the user
  // to type and still allow external state update.
  const [pageInputValue, setPageInputValue] = useState(
    String(currentPageNumber),
  );

  // Keep input in sync when the page changes via external state.
  useEffect(() => {
    setPageInputValue(String(currentPageNumber));
  }, [currentPageNumber]);

  const goToPageIndex = (pageIndex: number) => {
    const nextPageIndex = Math.min(Math.max(pageIndex, 0), totalPages - 1);

    if (nextPageIndex === currentPageIndex) {
      return;
    }

    setCurrentPageIndex(nextPageIndex);
  };

  const goToInputPage = () => {
    const trimmedValue = pageInputValue.trim();

    if (trimmedValue === "") {
      setPageInputValue(String(currentPageNumber));
      return;
    }

    const pageNumber = Number(trimmedValue);

    if (!Number.isInteger(pageNumber)) {
      setPageInputValue(String(currentPageNumber));
      return;
    }

    goToPageIndex(pageNumber - 1);
  };

  const pageInputWidth = `${3 + Math.max(String(totalPages).length, 2)}ch`;

  if (totalPages <= 1) {
    return null;
  }

  return (
    <Flex align="center" gap={2} flexWrap="nowrap">
      <Flex align="center" gap={1} flexShrink={0}>
        <IconButton
          type="button"
          size="sm"
          variant="outline"
          bgColor="white"
          onClick={() => goToPageIndex(currentPageIndex - 1)}
          icon={<MdChevronLeft />}
          aria-label="Previous page"
          isDisabled={currentPageNumber === 1}
        />

        <Flex align="center" gap={1.5} flexShrink={0}>
          <NumberInput
            bgColor="white"
            value={pageInputValue}
            onChange={setPageInputValue}
            onBlur={goToInputPage}
            min={1}
            max={totalPages}
            clampValueOnBlur={true}
            size="sm"
            w={pageInputWidth}
          >
            <NumberInputField
              inputMode="numeric"
              pe={3}
              textAlign="center"
              onKeyDown={(event) => {
                if (event.key !== "Enter") {
                  return;
                }

                event.preventDefault();
                event.currentTarget.blur();
              }}
            />
          </NumberInput>

          <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">
            of {totalPages}
          </Text>
        </Flex>

        <IconButton
          type="button"
          size="sm"
          variant="outline"
          bgColor="white"
          onClick={() => goToPageIndex(currentPageNumber)}
          icon={<MdChevronRight />}
          aria-label="Next page"
          isDisabled={currentPageNumber === totalPages}
        />
      </Flex>
    </Flex>
  );
}
